import { provideApolloClient } from "@vue/apollo-composable";

import gaiupApolloClient from "./ApolloClient";

/**
 * Creates a Vue Apollo composable outside a component setup, without destroying the app's default
 * client on the way out.
 *
 * `provideApolloClient(client)` does two things: it sets a module-level default, and it returns a
 * runner that clears that default once the callback finishes. `main.ts` relies on the first half —
 * it calls `provideApolloClient(gaiupApolloClient)` at startup so stores and providers can create
 * composables at all — and the idiom this replaces used the second half:
 *
 *     const withApollo = provideApolloClient(gaiupApolloClient);   // sets the default
 *     withApollo(() => useSomeQuery(...));                          // …and CLEARS it on exit
 *
 * So the first provider to run wiped the default for everything created after it. What that costs is
 * invisible until a composable resolves its client LATE: `useQuery` captures the client map at
 * creation and re-reads it when an `enabled` flag flips, in a `nextTick` where there is no component
 * instance to inject from. A map captured as `{}` then throws
 * "Apollo client with id default not found" — which is what the create flow logged on its way to the
 * settings page, and what a second `withApollo` call in the same provider was already risking.
 *
 * Restoring the default afterwards keeps both halves working: the callback runs with a client, and
 * the app still has one when the callback is done.
 */
export const withApolloClient = <T>(create: () => T): T => {
  const runWithClient = provideApolloClient(gaiupApolloClient);

  try {
    return runWithClient(create);
  } finally {
    // Not an else-branch of the try: a throw inside `create` skips the runner's own cleanup, so the
    // default would survive that path anyway — but re-setting it is correct either way and says so.
    provideApolloClient(gaiupApolloClient);
  }
};

export default withApolloClient;
